import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { Box, Stack, Typography, Chip, CircularProgress } from "@mui/material";
import { FiCalendar, FiInbox } from "react-icons/fi";
import dayjs from "dayjs";
import { T } from "../../constants/todoTokens";
import FullTaskCard from "./FullTaskCard";

const ScheduleView = ({
  tasks = [], scheduleDate, loading, loadingMore, hasMore,
  onLoadMore,
  onTaskClick, onArchive, onDelete, isAdmin, currentUserId, onAssigneeStatusChanged,
}) => {
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => { onLoadMoreRef.current = onLoadMore; }, [onLoadMore]);

  const observerRef = useRef(null);
  const triggerRef  = useCallback((el) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onLoadMoreRef.current?.(); },
      { threshold: 0.1 }
    );
    observerRef.current.observe(el);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerIndex = useMemo(() => Math.max(0, tasks.length - 6), [tasks.length]);
  const date         = useMemo(() => scheduleDate ?? dayjs(), [scheduleDate]);
  const isToday      = useMemo(() => date.isSame(dayjs(), "day"), [date]);

  return (
    <Box>
      {/* Date header */}
      <Stack
        direction="row" alignItems="center" gap={1.5}
        sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${T.border}`, bgcolor: T.surface }}
      >
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: T.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FiCalendar size={16} color={T.primary} />
        </Box>
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: T.text, lineHeight: 1.2 }}>
              {date.format("dddd, MMMM D, YYYY")}
            </Typography>
            {isToday && (
              <Chip label="Today" size="small" sx={{ height: 18, fontSize: "10px", fontWeight: 700, bgcolor: T.primaryLight, color: T.primaryDark, borderRadius: "5px" }} />
            )}
          </Stack>
          {!loading && (
            <Typography sx={{ fontSize: "12px", color: T.textMuted, mt: "2px" }}>
              {tasks.length > 0
                ? `${tasks.length} task${tasks.length !== 1 ? "s" : ""}${hasMore ? "+" : ""} from this date`
                : "No tasks found"}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Initial loader */}
      {loading && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, gap: 2 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: T.primary }} />
          <Typography sx={{ fontSize: "13px", color: T.textMuted, fontWeight: 600 }}>Loading tasks…</Typography>
        </Box>
      )}

      {/* Empty state */}
      {!loading && tasks.length === 0 && (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: "18px", bgcolor: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <FiInbox size={28} color="#CBD5E1" />
          </Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: T.textSub }}>No tasks found for this date</Typography>
          <Typography sx={{ mt: 0.5, fontSize: "13px", color: T.textMuted }}>Try selecting a different date from the calendar</Typography>
        </Box>
      )}

      {/* Task list — no animation, renders instantly */}
      {!loading && tasks.length > 0 && (
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.5}>
            {tasks.map((task, i) => (
              <Box key={task._id} ref={hasMore && i === triggerIndex ? triggerRef : null}>
                <FullTaskCard
                  task={task}
                  index={i}
                  onClick={onTaskClick}
                  onArchive={onArchive}
                  onDelete={onDelete}
                  isAdmin={isAdmin}
                  currentUserId={currentUserId}
                  onAssigneeStatusChanged={onAssigneeStatusChanged}
                />
              </Box>
            ))}
          </Stack>

          {/* Load-more spinner */}
          {loadingMore && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 4, gap: 1.5 }}>
              <CircularProgress size={32} thickness={3.5} sx={{ color: T.primary }} />
              <Typography sx={{ fontSize: "12px", color: T.textMuted, fontWeight: 500 }}>Loading more…</Typography>
            </Box>
          )}

          {/* End of list */}
          {!hasMore && !loadingMore && (
            <Typography sx={{ textAlign: "center", fontSize: "12px", color: T.textMuted, py: 2.5, borderTop: `1px dashed ${T.border}`, mt: 1 }}>
              No more tasks available
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ScheduleView);
